import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Waves, Fingerprint, Database, HardDrive, BellRing, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const ICONS = { well: Waves, sso: Fingerprint, mongodb: Database, blob: HardDrive, push: BellRing };
const DESCRIPTIONS = {
  well: "Primary family health platform. Forge secures its privacy pipeline and syncs via a shared secret.",
  sso: "Precipice ID single sign-on identity provider for federated authentication.",
  mongodb: "Primary datastore for configuration, audit logs and operational metrics.",
  blob: "Encrypted object storage for user-submitted files and media artifacts.",
  push: "Web Push (VAPID) delivery channel for security and privacy notifications.",
};
const LINKS = { well: "https://wellkept.family", sso: "https://id.precipice-health.com" };

const STATUS = {
  operational: { label: "Operational", cls: "text-emerald-500 border-emerald-500/30 bg-emerald-500/5" },
  degraded: { label: "Degraded", cls: "text-yellow-500 border-yellow-500/30 bg-yellow-500/5" },
  down: { label: "Down", cls: "text-destructive border-destructive/30 bg-destructive/5" },
};

export default function Integrations() {
  const { data } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
    refetchInterval: 15000,
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl" data-testid="integrations-grid">
      {(data?.services || []).map((s, i) => {
        const Icon = ICONS[s.key] || Database;
        const st = STATUS[s.status] || STATUS.down;
        return (
          <div key={s.key} data-testid={`integration-${s.key}`}
            className="border border-border rounded-sm bg-card p-6 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-primary/10 border border-primary/25 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{s.key}</div>
                </div>
              </div>
              <span className={`font-mono text-[10px] uppercase tracking-[0.1em] px-2 py-1 rounded-sm border ${st.cls}`}>
                {st.label}
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">{DESCRIPTIONS[s.key]}</p>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="font-mono text-[11px] text-muted-foreground truncate max-w-[70%]">{s.detail}</span>
              {LINKS[s.key] && (
                <a href={LINKS[s.key]} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 font-mono text-[11px] text-primary hover:underline">
                  visit <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
